<template>
  <el-dialog
    custom-class="export-compliance-dialog"
    :visible.sync="visibleSync"
    width="820px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    append-to-body
  >
    <div slot="title" class="ec-title">
      {{ $t('exportCompliance.dialogTitle') }}
    </div>
    <div class="ec-body">
      <div v-if="level !== null && level !== undefined" class="ec-risk-level" :class="levelClass">
        Risk Level: {{ levelText }} (Level {{ level }})
      </div>
      <div v-if="conclusion" class="ec-conclusion">
        <span class="ec-conclusion-text">{{ conclusion }}</span>
      </div>
      <div v-if="groupedDetails.length" class="ec-details">
        <div v-for="group in groupedDetails" :key="group.type" class="ec-group">
          <div class="ec-group-header" @click="toggleGroup(group.type)">
            <i :class="collapsed[group.type] ? 'el-icon-arrow-right' : 'el-icon-arrow-down'" />
            <span class="ec-group-title">{{ group.title }} ({{ group.type }})</span>
          </div>
          <div v-show="!collapsed[group.type]" class="ec-group-content">
            <div v-for="(item, index) in group.items" :key="index" class="ec-item">
              <div class="ec-item-line">
                ・{{ item.validatedItem }}：{{ item.validatedContent }}
              </div>
              <div class="ec-item-output">
                Risk Output: {{ item.riskOutput }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div slot="footer" class="ec-footer">
      <el-button class="ec-btn-cancel" @click="handleCancel">
        {{ $t('exportCompliance.cancel') }}
      </el-button>
      <el-button class="ec-btn-confirm" type="primary" @click="handleConfirm">
        {{ confirmText }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
const LEVEL_TEXT = {
  0: 'No Risk',
  1: 'Low Risk',
  2: 'Medium Risk',
  3: 'High Risk',
  4: 'Prohibited Transaction'
};

const TYPE_TITLE = {
  SUBJECT: 'Subject Verification',
  ITEM: 'Item Verification',
  COUNTRY: 'Embargo Country Verification',
  PURPOSE: 'End-Use Verification'
};

// 明细展示的最低风险等级：低于该等级的明细不展示（等级缺失时保留，避免误过滤）
const MIN_DETAIL_LEVEL = 2;

export default {
  name: 'ExportComplianceDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // confirm=2级确认弹窗；review=3/4级发起审查弹窗；error=检测异常人工放行弹窗
    mode: {
      type: String,
      default: 'confirm'
    },
    level: {
      type: Number,
      default: null
    },
    conclusion: {
      type: String,
      default: ''
    },
    details: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      collapsed: {}
    };
  },
  computed: {
    visibleSync: {
      get () {
        return this.visible;
      },
      set (val) {
        this.$emit('update:visible', val);
      }
    },
    levelText () {
      return LEVEL_TEXT[this.level] || '';
    },
    levelClass () {
      if (this.level >= 4) {
        return 'ec-level-danger';
      }
      if (this.level === 3) {
        return 'ec-level-high';
      }
      if (this.level === 2) {
        return 'ec-level-medium';
      }
      return 'ec-level-low';
    },
    confirmText () {
      if (this.mode === 'review') {
        return this.$t('exportCompliance.submitReview');
      }
      if (this.mode === 'error') {
        return this.$t('exportCompliance.continue');
      }
      return this.$t('exportCompliance.confirm');
    },
    groupedDetails () {
      const groups = [];
      const map = {};
      (this.details || []).forEach(item => {
        // 风险等级低于 2 级的明细不展示；等级缺失时保留，避免误过滤
        const level = Number(item.riskLevel);
        const hasLevel = item.riskLevel !== null && item.riskLevel !== undefined
          && item.riskLevel !== '' && !isNaN(level);
        if (hasLevel && level < MIN_DETAIL_LEVEL) {
          return;
        }
        const type = item.validationType || 'OTHER';
        if (!map[type]) {
          map[type] = { type: type, title: TYPE_TITLE[type] || type, items: [] };
          groups.push(map[type]);
        }
        map[type].items.push(item);
      });
      return groups;
    }
  },
  methods: {
    toggleGroup (type) {
      this.$set(this.collapsed, type, !this.collapsed[type]);
    },
    handleCancel () {
      this.$emit('cancel');
    },
    handleConfirm () {
      if (this.mode === 'review') {
        this.$emit('submit-review');
      } else {
        this.$emit('confirm');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ec-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2d3d;
}

.ec-body {
  padding: 0 4px;
  max-height: 460px;
  overflow-y: auto;
}

.ec-risk-level {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;

  &.ec-level-danger {
    color: #e60012;
  }

  &.ec-level-high {
    color: #e60012;
  }

  &.ec-level-medium {
    color: #e6a23c;
  }

  &.ec-level-low {
    color: #67c23a;
  }
}

.ec-conclusion {
  font-size: 13px;
  color: #5a5e66;
  line-height: 1.6;
  margin-bottom: 14px;
  word-break: break-word;
}

.ec-group {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 10px;

  .ec-group-header {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 12px;
    background: #fafafa;
    cursor: pointer;
    border-bottom: 1px solid #ebeef5;

    i {
      margin-right: 8px;
      color: #909399;
    }

    .ec-group-title {
      font-size: 13px;
      color: #1f2d3d;
      font-weight: 600;
    }
  }

  .ec-group-content {
    padding: 10px 12px;
  }

  .ec-item {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .ec-item-line {
      font-size: 13px;
      color: #5a5e66;
      line-height: 1.6;
      word-break: break-word;
    }

    .ec-item-output {
      font-size: 13px;
      color: #e60012;
      line-height: 1.6;
      margin-top: 2px;
      word-break: break-word;
    }
  }
}

.ec-footer {
  text-align: right;

  .ec-btn-cancel {
    min-width: 96px;
  }

  .ec-btn-confirm {
    min-width: 120px;
    background-color: #009688;
    border-color: #009688;
  }
}
</style>
